import { API } from '../api.js';
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Avatar from '../components/Avatar';
import styles from './AboutTab.module.css';
import MarkdownRenderer from '../components/MarkdownRenderer';
import { useAuth } from '../contexts/AuthContext';

export default function AboutTab({ community, onToast }) {
  const navigate = useNavigate();
  const { user, token } = useAuth();

  const coverFileInputRef = useRef(null);
  const aboutImagesInputRef = useRef(null);

  const [activeThumb, setActiveThumb] = useState(0);
  const [counts,  setCounts]  = useState(null);
  const [members, setMembers] = useState([]);

  const [settingsModal, setSettingsModal] = useState(false);
  const [settingsForm, setSettingsForm] = useState({
    name: '',
    description: '',
    isPrivate: false,
    coverImageUrl: '',
    aboutImages: [],
    note: ''
  });
  const [savingSettings, setSavingSettings] = useState(false);

  useEffect(() => {
    if (community) {
      setSettingsForm({
        name: community.name || '',
        description: community.description || '',
        isPrivate: community.isPrivate ?? false,
        coverImageUrl: community.coverImageUrl || '',
        aboutImages: community.aboutImages || [],
        note: community.note || '',
      });
    }
  }, [community]);

  async function saveCommunitySettings() {
    if (!settingsForm.name.trim()) return;
    setSavingSettings(true);
    try {
      const res = await fetch(`${API}/api/communities/${community.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(settingsForm),
      });
      if (res.ok) {
        if (onToast) onToast('✓ Community settings updated!');
        setSettingsModal(false);
        window.location.reload();
      } else {
        if (onToast) onToast('Failed to update community settings');
      }
    } catch {
      if (onToast) onToast('Error updating settings');
    }
    setSavingSettings(false);
  }

  async function handleCoverUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    const ext = file.name.substring(file.name.lastIndexOf('.'));
    try {
      if (onToast) onToast('Uploading cover picture...');
      const res = await fetch(`${API}/api/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'X-File-Ext': ext },
        body: file
      });
      if (res.ok) {
        const data = await res.json();
        setSettingsForm(prev => ({ ...prev, coverImageUrl: data.url }));
        if (onToast) onToast('✓ Cover picture uploaded!');
      } else {
        if (onToast) onToast('Failed to upload cover picture');
      }
    } catch {
      if (onToast) onToast('Error uploading cover picture');
    }
  }

  async function handleAboutImagesUpload(e) {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    if (onToast) onToast('Uploading attachment images...');
    try {
      const promises = files.map(async (file) => {
        const ext = file.name.substring(file.name.lastIndexOf('.'));
        const res = await fetch(`${API}/api/upload`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'X-File-Ext': ext },
          body: file
        });
        if (!res.ok) throw new Error();
        const data = await res.json();
        return data.url;
      });
      const urls = await Promise.all(promises);
      setSettingsForm(prev => ({
        ...prev,
        aboutImages: [...(prev.aboutImages || []), ...urls]
      }));
      if (onToast) onToast('✓ Attachment images uploaded!');
    } catch {
      if (onToast) onToast('Failed to upload some images');
    }
  }

  const name        = community?.name        || 'Community Name';
  const description = community?.description || '';
  const note        = community?.note        || '';
  const slug        = community?.slug        || String(community?.id || 'community');
  const accentColor = community?.color       || null;
  const isPrivate   = community?.isPrivate   ?? false;
  const pricing     = community?.pricing     || 'free';
  const ownerName   = community?.ownerName   || null;

  useEffect(() => {
    if (!community?.id) return;
    fetch(`${API}/api/communities/${community.id}/members`)
      .then(r => r.ok ? r.json() : [])
      .then(data => {
        if (!Array.isArray(data)) return;
        setMembers(data);
        setCounts({
          member_count: data.length,
          admin_count:  data.filter(m => m.role === 'admin').length,
          online_count: data.filter(m => m.isOnline).length,
        });
      })
      .catch(() => {});
  }, [community?.id]);

  const memberCount = counts?.member_count ?? 0;
  const adminCount  = counts?.admin_count  ?? 0;
  const onlineCount = counts?.online_count ?? 0;

  const images      = Array.isArray(community?.aboutImages) && community.aboutImages.length > 0
    ? community.aboutImages : [];
  const currentImage = images.length > 0 ? images[activeThumb] : (community?.coverImageUrl || null);
  const placeholders = [0, 1, 2, 3, 4];

  return (
    <div className={styles.grid}>

      {/* ── Left column: media + note ───────────────────────────────── */}
      <div className={styles.leftCol}>

        {/* Community title */}
        <h2 className={styles.pageTitle}>{name}</h2>

        {/* Hero media */}
        <div
          className={styles.hero}
          style={accentColor ? { background: `linear-gradient(135deg, ${accentColor}cc, ${accentColor}44)` } : undefined}
        >
          {currentImage
            ? <img src={`${API}${currentImage}`} alt="Hero" className={styles.heroImg} />
            : <>
                <div className={styles.heroGradient}
                  style={accentColor ? { background: `linear-gradient(135deg, ${accentColor}dd, ${accentColor}55)` } : undefined} />
                <div className={styles.heroEmoji}>{community?.emoji || '🎓'}</div>
              </>
          }
          {!images.length && (
            <>
              <button className={styles.playBtn} aria-label="Play">▶</button>
              <span className={styles.duration}>3 min</span>
            </>
          )}
        </div>

        {/* Thumbnail strip */}
        <div className={styles.thumbRow}>
          {images.length > 0
            ? images.map((url, i) => (
                <button key={i}
                  className={`${styles.thumb} ${activeThumb === i ? styles.thumbActive : ''}`}
                  onClick={() => setActiveThumb(i)}
                >
                  <img src={`${API}${url}`} alt="" className={styles.thumbImg} />
                </button>
              ))
            : placeholders.map((_, i) => (
                <button key={i}
                  className={`${styles.thumb} ${styles.thumbPlaceholder} ${activeThumb === i ? styles.thumbActive : ''}`}
                  onClick={() => setActiveThumb(i)}
                  style={accentColor ? { background: `${accentColor}${22 + i * 8}` } : undefined}
                >
                  {i === 0 && <span className={styles.thumbPlayMini}>▶</span>}
                </button>
              ))
          }
        </div>

        {/* Meta row: private/public · members · free/paid · by owner */}
        <div className={styles.metaRow}>
          <span className={styles.metaChip}>
            {isPrivate ? '🔒 Private' : '🌐 Public'}
          </span>
          <span className={styles.metaDot}>·</span>
          <span className={styles.metaChip}>
            👥 {memberCount.toLocaleString()} members
          </span>
          <span className={styles.metaDot}>·</span>
          <span className={styles.metaChip}>
            🏷️ {pricing === 'free' ? 'Free' : `$${community?.price ?? 0}/mo`}
          </span>
          {ownerName && (
            <>
              <span className={styles.metaDot}>·</span>
              <span className={styles.metaChip}>By {ownerName}</span>
            </>
          )}
        </div>

        {/* Note content */}
        {note ? (
          <div className={styles.noteContent}>
            <MarkdownRenderer text={note} />
          </div>
        ) : (
          description && <p className={styles.noteContent}>{description}</p>
        )}
      </div>

      {/* ── Right column: community info card ───────────────────────── */}
      <div className={styles.rightCol}>
        <div className={styles.infoCard}>

          {/* Cover banner */}
          <div
            className={styles.banner}
            style={community?.coverImageUrl
              ? { backgroundImage: `url(${API}${community.coverImageUrl})` }
              : accentColor
                ? { background: `linear-gradient(135deg, ${accentColor}ee, ${accentColor}88)` }
                : undefined}
          >
            {!community?.coverImageUrl && (
              <>
                <div className={styles.bannerTitle}>{name.toUpperCase()}</div>
                <div className={styles.bannerSub}>WELCOME TO OUR COMMUNITY</div>
              </>
            )}
          </div>

          <div className={styles.infoBody}>
            <h3 className={styles.communityName}>{name}</h3>
            <a href={`https://qeduvo.com/${slug}`} target="_blank" rel="noreferrer" className={styles.communityUrl}>
              qeduvo.com/{slug}
            </a>

            {description && <p className={styles.description}>{description}</p>}

            {/* Quick links */}
            <div className={styles.links}>
              <button 
                onClick={() => navigate(`/${slug}/community`)} 
                className={styles.link}
                style={{ 
                  background: 'none', 
                  border: 'none', 
                  padding: 0, 
                  textAlign: 'left', 
                  font: 'inherit', 
                  cursor: 'pointer', 
                  display: 'inline-block',
                  width: 'fit-content'
                }}
              >
                🔗 View Community Feed
              </button>
              <button 
                onClick={() => navigate(`/${slug}/classroom`)} 
                className={styles.link}
                style={{ 
                  background: 'none', 
                  border: 'none', 
                  padding: 0, 
                  textAlign: 'left', 
                  font: 'inherit', 
                  cursor: 'pointer', 
                  display: 'inline-block',
                  width: 'fit-content'
                }}
              >
                🔗 View Available Courses
              </button>
            </div>

            <div className={styles.divider} />

            {/* Stats */}
            <div className={styles.statsRow}>
              <div className={styles.stat}>
                <span className={styles.statVal}>{memberCount.toLocaleString()}</span>
                <span className={styles.statLbl}>Members</span>
              </div>
              <div className={styles.statSep} />
              <div className={styles.stat}>
                <span className={styles.statVal}>{onlineCount.toLocaleString()}</span>
                <span className={styles.statLbl}>Online</span>
              </div>
              <div className={styles.statSep} />
              <div className={styles.stat}>
                <span className={styles.statVal}>{adminCount.toLocaleString()}</span>
                <span className={styles.statLbl}>Admins</span>
              </div>
            </div>

            <div className={styles.divider} />

            {/* Avatar stack */}
            {members.length > 0 && (
              <div className={styles.avatarStack}>
                {members.slice(0, 6).map((m, i) => (
                  <div key={m.id ?? i} style={{ marginLeft: i > 0 ? -8 : 0, zIndex: 6 - i }}>
                    <Avatar
                      user={{ avatar: m.avatarUrl || m.avatar_url, avatarEmoji: m.avatarEmoji, displayName: m.displayName || m.display_name }}
                      size={28}
                    />
                  </div>
                ))}
                {memberCount > 6 && (
                  <span className={styles.avatarMore}>+{(memberCount - 6).toLocaleString()}</span>
                )}
              </div>
            )}

            {user && Number(user.id) === Number(community?.ownerId) && (
              <button 
                onClick={() => setSettingsModal(true)} 
                className="btn btn-ghost" 
                style={{ width: '100%', border: '1px solid var(--border)', fontSize: 12, fontWeight: 600, padding: '10px', borderRadius: '8px', cursor: 'pointer', background: 'transparent', color: 'var(--text)', marginTop: '16px' }}
              >
                ⚙️ SETTINGS
              </button>
            )}

            <p className={styles.poweredBy}>
              Powered by <strong>Qeduvo</strong>
            </p>
          </div>
        </div>
      </div>

      {/* Settings Modal */}
      {settingsModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setSettingsModal(false)}>
          <div className="modal" style={{ maxWidth: 720, borderRadius: 12, maxHeight: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {/* Modal Header */}
            <div style={{ padding: '20px 30px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
              <h2 style={{ fontSize: 20, margin: 0, fontWeight: 800 }}>⚙️ Community Settings</h2>
              <button onClick={() => setSettingsModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: 'var(--text3)' }}>×</button>
            </div>

            {/* Scrollable Content */}
            <div className="modal-body" style={{ padding: '24px 30px', overflowY: 'auto', flex: 1 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div>
                  <label style={{ fontSize: 13, fontWeight: 700, display: 'block', marginBottom: 6 }}>Community Name</label>
                  <input
                    type="text"
                    value={settingsForm.name}
                    onChange={e => setSettingsForm({ ...settingsForm, name: e.target.value })}
                    style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface2)', color: 'var(--text)' }}
                  />
                </div>

                <div>
                  <label style={{ fontSize: 13, fontWeight: 700, display: 'block', marginBottom: 6 }}>Short Description</label>
                  <textarea
                    rows={3}
                    value={settingsForm.description}
                    onChange={e => setSettingsForm({ ...settingsForm, description: e.target.value })}
                    style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface2)', color: 'var(--text)', resize: 'vertical', fontFamily: 'inherit' }}
                  />
                </div>

                <div>
                  <label style={{ fontSize: 13, fontWeight: 700, display: 'block', marginBottom: 4 }}>About Note <span style={{ fontWeight: 400, fontSize: 11, color: 'var(--text3)' }}>(shown on About tab — supports Markdown: headers, lists, bold, italics, links, and code)</span></label>
                  <textarea
                    rows={8}
                    placeholder={'🎯 What you\'ll learn:\n- Lesson 1\n- Lesson 2\n\n✅ What you\'ll get:\n- Benefit A\n- Benefit B'}
                    value={settingsForm.note}
                    onChange={e => setSettingsForm({ ...settingsForm, note: e.target.value })}
                    style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface2)', color: 'var(--text)', resize: 'vertical', fontFamily: 'inherit', fontSize: 13, lineHeight: 1.6 }}
                  />
                </div>

                {/* Cover Picture Background */}
                <div>
                  <label style={{ fontSize: 13, fontWeight: 700, display: 'block', marginBottom: 6 }}>Cover Picture Background</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    {settingsForm.coverImageUrl && (
                      <img src={`${API}${settingsForm.coverImageUrl}`} alt="Cover" style={{ width: 80, height: 45, objectFit: 'cover', borderRadius: 6, border: '1px solid var(--border)' }} />
                    )}
                    <button className="btn btn-ghost btn-sm" onClick={() => coverFileInputRef.current?.click()} style={{ border: '1px solid var(--border)', fontSize: 12 }}>
                      📁 Upload Cover Image
                    </button>
                    <input type="file" ref={coverFileInputRef} style={{ display: 'none' }} accept="image/*" onChange={handleCoverUpload} />
                  </div>
                </div>

                {/* Attachment Pictures (About Page Images) */}
                <div>
                  <label style={{ fontSize: 13, fontWeight: 700, display: 'block', marginBottom: 6 }}>Attachment Pictures (About Page Images)</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
                    {(settingsForm.aboutImages || []).map((img, idx) => (
                      <div key={idx} style={{ position: 'relative', width: 60, height: 60 }}>
                        <img src={`${API}${img}`} alt={`About ${idx}`} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 6 }} />
                        <button
                          onClick={() => setSettingsForm(prev => ({ ...prev, aboutImages: prev.aboutImages.filter((_, i) => i !== idx) }))}
                          style={{ position: 'absolute', top: -4, right: -4, background: 'red', color: 'white', border: 'none', borderRadius: '50%', width: 16, height: 16, fontSize: 10, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold' }}
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                  <button className="btn btn-ghost btn-sm" onClick={() => aboutImagesInputRef.current?.click()} style={{ border: '1px solid var(--border)', fontSize: 12 }}>
                    ➕ Add Images
                  </button>
                  <input type="file" ref={aboutImagesInputRef} style={{ display: 'none' }} accept="image/*" multiple onChange={handleAboutImagesUpload} />
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <input
                    type="checkbox"
                    id="isPrivate"
                    checked={settingsForm.isPrivate}
                    onChange={e => setSettingsForm({ ...settingsForm, isPrivate: e.target.checked })}
                    style={{ cursor: 'pointer' }}
                  />
                  <label htmlFor="isPrivate" style={{ fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>Make community private (requires invite link)</label>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div style={{ padding: '16px 30px 24px', display: 'flex', justifyContent: 'flex-end', gap: 12, borderTop: '1px solid var(--border)', flexShrink: 0 }}>
              <button
                className="btn btn-primary"
                onClick={saveCommunitySettings}
                disabled={savingSettings || !settingsForm.name.trim()}
                style={{ background: 'var(--accent)', color: '#fff', border: 'none' }}
              >
                {savingSettings ? 'SAVING...' : 'SAVE SETTINGS'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
